import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { Button } from "../components/atoms";
import { createDepartment, fetchDepartments, updateDepartment } from "../api/departments";
import {
    DEPARTMENT_CODE_MAX_LENGTH,
    DEPARTMENT_NAME_MAX_LENGTH,
    DEPARTMENT_SHORT_NAME_MAX_LENGTH,
    validateDepartmentCode,
    validateDepartmentName,
    validateDepartmentShortName,
    type DepartmentFormOutletContext,
    type DepartmentFormState,
} from "../components/DepartmentEditLayout";

/**
 * フォームフィールドラッパー
 */
const FormField = ({
    label,
    required = false,
    children,
}: {
    label: string;
    required?: boolean;
    children: React.ReactNode;
}) => (
    <div className="flex items-start gap-6">
        <label className="w-28 text-sm text-gray-700 pt-2 flex-shrink-0">
            {label}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        <div className="flex-1">{children}</div>
    </div>
);

/**
 * 部門登録・編集ページ（基本情報タブ）
 */
const DepartmentFormPage = () => {
    const navigate = useNavigate();
    const {
        setPageError,
        invalidFields,
        setInvalidFields,
        pendingFormState,
        setPendingFormState,
    } = useOutletContext<DepartmentFormOutletContext>();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const departmentId = id ? parseInt(id, 10) : null;

    // 編集モードで pendingFormState が無い場合のみ DB 取得
    const needsFetch = isEdit && departmentId !== null && pendingFormState === null;
    const [loading, setLoading] = useState(needsFetch);

    const [formData, setFormData] = useState<DepartmentFormState>({
        code: pendingFormState?.code ?? "",
        name: pendingFormState?.name ?? "",
        shortName: pendingFormState?.shortName ?? "",
    });

    const [saving, setSaving] = useState(false);

    // 編集時にデータを取得（pendingFormState 未設定時だけ）
    // 暫定: 専用の fetchDepartment エンドポイントが無いため一覧取得 + id 検索
    useEffect(() => {
        if (!needsFetch || departmentId === null) return;
        const loadData = async () => {
            setLoading(true);
            setPageError(null);
            try {
                const res = await fetchDepartments();
                const dept = res.items.find((d) => d.id === departmentId);
                if (!dept) {
                    setPageError("部門が見つかりません");
                    return;
                }
                const next: DepartmentFormState = {
                    code: dept.code,
                    name: dept.name,
                    shortName: "", // バックエンド未連携
                };
                setFormData(next);
                setPendingFormState(next);
            } catch (err) {
                console.error("Failed to fetch department:", err);
                setPageError(err instanceof Error ? err.message : "部門データの取得に失敗しました");
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [needsFetch, departmentId, setPageError, setPendingFormState]);

    const handleInputChange = useCallback(
        <K extends keyof DepartmentFormState>(field: K, value: DepartmentFormState[K]) => {
            setFormData((prev) => {
                const updated = { ...prev, [field]: value };
                setPendingFormState(updated);
                return updated;
            });
        },
        [setPendingFormState],
    );

    const isFieldInvalid = (key: keyof DepartmentFormState): boolean => {
        if (!invalidFields.includes(key)) return false;
        const value = formData[key];
        return !value || !value.trim();
    };

    const validateRequired = (state: DepartmentFormState): boolean => {
        const missing: { key: string; label: string }[] = [];
        if (!state.code.trim()) missing.push({ key: "code", label: "部門コード" });
        if (!state.name.trim()) missing.push({ key: "name", label: "部門名" });
        if (missing.length > 0) {
            setInvalidFields(missing.map((f) => f.key));
            setPageError(`必須項目が入力されていません: ${missing.map((f) => f.label).join(", ")}`);
            return false;
        }
        return true;
    };

    // 書式エラー（ライブ検証）
    const codeError = validateDepartmentCode(formData.code);
    const nameError = validateDepartmentName(formData.name);
    const shortNameError = validateDepartmentShortName(formData.shortName);
    const hasFormatError =
        codeError !== null || nameError !== null || shortNameError !== null;

    const handleCancel = () => {
        navigate("/org-settings/department");
    };

    const handleComplete = async () => {
        if (!validateRequired(formData)) return;

        setInvalidFields([]);
        setPageError(null);
        setSaving(true);
        try {
            // 略称はバックエンドに保存先がないので送らない（フォームには残すが捨てる）
            if (isEdit && departmentId !== null) {
                await updateDepartment(departmentId, {
                    code: formData.code,
                    name: formData.name,
                });
            } else {
                await createDepartment({
                    code: formData.code,
                    name: formData.name,
                });
            }
            navigate("/org-settings/department");
        } catch (err) {
            console.error("Failed to save department:", err);
            setPageError(err instanceof Error ? err.message : "保存に失敗しました");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <span className="text-gray-500">読み込み中...</span>
            </div>
        );
    }

    return (
        <div className="flex-1 flex flex-col bg-white rounded-lg overflow-hidden">
            <div className="flex-1 overflow-auto p-6">
                <div className="space-y-6">
                    <div className="text-xs text-red-500">「*」は登録必須項目です。</div>

                    {/* 部門コード */}
                    <FormField label="部門コード" required>
                        <input
                            type="text"
                            value={formData.code}
                            onChange={(e) => handleInputChange("code", e.target.value)}
                            placeholder="001"
                            className={clsx(
                                "form-input-field w-60",
                                (isFieldInvalid("code") || codeError !== null) && "border-red-500",
                            )}
                        />
                        {codeError !== null && (
                            <p className="mt-1 text-xs text-red-500">
                                半角英数字{DEPARTMENT_CODE_MAX_LENGTH}桁以内
                            </p>
                        )}
                    </FormField>

                    {/* 部門名 */}
                    <FormField label="部門名" required>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                            placeholder="キッチン"
                            className={clsx(
                                "form-input-field w-60",
                                (isFieldInvalid("name") || nameError !== null) && "border-red-500",
                            )}
                        />
                        {nameError !== null && (
                            <p className="mt-1 text-xs text-red-500">
                                {DEPARTMENT_NAME_MAX_LENGTH}文字以内
                            </p>
                        )}
                    </FormField>

                    {/* 略称（現在はバックエンド未対応のため非活性） */}
                    <FormField label="略称">
                        <input
                            type="text"
                            value={formData.shortName}
                            onChange={(e) => handleInputChange("shortName", e.target.value)}
                            placeholder="キッチン"
                            disabled
                            className={clsx(
                                "form-input-field w-60",
                                "bg-gray-100 text-gray-500 cursor-not-allowed",
                                shortNameError !== null && "border-red-500",
                            )}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            現在は保存されません
                        </p>
                        {shortNameError !== null && (
                            <p className="mt-1 text-xs text-red-500">
                                {DEPARTMENT_SHORT_NAME_MAX_LENGTH}文字以内
                            </p>
                        )}
                    </FormField>
                </div>
            </div>

            {/* フッターボタン */}
            <div className="flex justify-end gap-4 px-6 py-4">
                <Button variant="outline" size="md" onClick={handleCancel} className="px-8">
                    キャンセル
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleComplete}
                    disabled={saving || hasFormatError}
                    className="px-8"
                >
                    {saving ? (isEdit ? "更新中..." : "登録中...") : (isEdit ? "設定を完了する" : "登録する")}
                </Button>
            </div>
        </div>
    );
};

export default DepartmentFormPage;
